import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { noticesApi } from "./api";
import type { NoticeRequest } from "./types";

// Shared query keys -- the Dashboard's Notice Board widget and the admin page both
// read through these, and every mutation below invalidates them, so creating,
// editing, approving or rejecting a notice shows up immediately without needing a
// hard refresh (previously the admin page used its own ad-hoc fetch/reload and never
// touched the dashboard's cached copy, so the dashboard could sit stale for minutes).
export const NOTICES_ACTIVE_KEY = ["notices-active-dashboard"];
export const NOTICES_LIST_KEY = "notices-admin-list";

/** Dashboard Notice Board widget -- short staleTime since this is the one place a
 *  notice's own author expects to see their change land. Mutations below invalidate
 *  it immediately anyway; the staleTime here only governs how soon *other* users'
 *  changes surface without an explicit action. */
export function useActiveNotices() {
  return useQuery({
    queryKey: NOTICES_ACTIVE_KEY,
    queryFn: noticesApi.active,
    staleTime: 1000 * 60,
  });
}

export function useNoticesList(page: number, size: number, status?: string) {
  return useQuery({
    queryKey: [NOTICES_LIST_KEY, page, size, status],
    queryFn: () => noticesApi.list(page, size, status),
    staleTime: 1000 * 30,
  });
}

function useInvalidateNotices() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: NOTICES_ACTIVE_KEY });
    void qc.invalidateQueries({ queryKey: [NOTICES_LIST_KEY] });
  };
}

export function useCreateNotice() {
  const invalidate = useInvalidateNotices();
  return useMutation({
    mutationFn: (payload: NoticeRequest) => noticesApi.create(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateNotice() {
  const invalidate = useInvalidateNotices();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: NoticeRequest }) => noticesApi.update(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteNotice() {
  const invalidate = useInvalidateNotices();
  return useMutation({
    mutationFn: (id: number) => noticesApi.remove(id),
    onSuccess: invalidate,
  });
}

export function useApproveNotice() {
  const invalidate = useInvalidateNotices();
  return useMutation({
    mutationFn: ({ id, remarks }: { id: number; remarks?: string | null }) => noticesApi.approve(id, remarks),
    onSuccess: invalidate,
  });
}

export function useRejectNotice() {
  const invalidate = useInvalidateNotices();
  return useMutation({
    mutationFn: ({ id, remarks }: { id: number; remarks?: string | null }) => noticesApi.reject(id, remarks),
    onSuccess: invalidate,
  });
}

// ── Personal pin / reminder — only the dashboard's own notices-active cache needs
// invalidating (the admin list doesn't show these per-viewer flags), so these mutations
// invalidate NOTICES_ACTIVE_KEY directly rather than pulling in useInvalidateNotices.

export function usePinNotice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => noticesApi.pin(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: NOTICES_ACTIVE_KEY }),
  });
}

export function useUnpinNotice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => noticesApi.unpin(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: NOTICES_ACTIVE_KEY }),
  });
}

export function useRequestNoticeReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => noticesApi.requestReminder(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: NOTICES_ACTIVE_KEY }),
  });
}

export function useCancelNoticeReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => noticesApi.cancelReminder(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: NOTICES_ACTIVE_KEY }),
  });
}
